/**
 * Convert our proposal blocks to craft.js serialized JSON format.
 * Our format: [{ id, type, props }, ...]
 * Craft format: { ROOT: { type: { resolvedName }, props, nodes }, [id]: {...} }
 */
const CRAFT_ROOT = 'ROOT';

/** Drag-and-drop layout blocks only; pricing, terms and CTA are appended automatically in the viewer */
export const AUTO_APPENDED_BLOCK_TYPES = ['pricing', 'terms', 'cta'];

const BLOCK_RESOLVER_NAMES = {
  heading: 'HeadingBlock',
  paragraph: 'ParagraphBlock',
  image: 'ImageBlock',
  divider: 'DividerBlock',
  row: 'RowBlock',
  column: 'ColumnBlock'
};

/** Old craft components (removed from editor) — map then strip */
const LEGACY_RESOLVER_TO_TYPE = {
  PricingBlock: 'pricing',
  TermsBlock: 'terms',
  CtaBlock: 'cta'
};

export function filterLayoutBlocks(blocks) {
  if (!blocks?.length) return [];
  return blocks
    .filter((b) => b && !AUTO_APPENDED_BLOCK_TYPES.includes(b.type))
    .map((b) => {
      const children = filterLayoutBlocks(b.children || []);
      return {
        ...b,
        ...(children.length > 0 ? { children } : {})
      };
    });
}

export function normaliseLayoutBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  const mapBlock = (block) => {
    if (!block || typeof block !== 'object') return null;
    const children = Array.isArray(block.children)
      ? block.children.map(mapBlock).filter(Boolean)
      : [];
    return {
      id: block.id || (block._id && String(block._id)),
      type: block.type || 'paragraph',
      props: block.props || {},
      ...(children.length > 0 ? { children } : {})
    };
  };
  return filterLayoutBlocks(blocks.map(mapBlock).filter(Boolean));
}

export function blocksToCraftJson(blocks) {
  const rootProps = { id: 'proposal-canvas', is: 'div', style: { minHeight: 280 } };
  const rootNode = {
    type: { resolvedName: 'Canvas' },
    isCanvas: true,
    props: rootProps,
    nodes: [],
    linkedNodes: {},
    parent: null,
    displayName: 'Canvas'
  };
  if (!blocks || blocks.length === 0) {
    const empty = { [CRAFT_ROOT]: rootNode };
    return JSON.stringify(empty);
  }

  let generatedId = 0;
  const getBlockId = (b) => {
    const id = b?.id || (b?._id && String(b._id));
    if (id) return String(id);
    generatedId += 1;
    return `block_${generatedId}`;
  };

  const validBlocks = filterLayoutBlocks(blocks).filter(Boolean);
  rootNode.nodes = [];
  const nodes = { [CRAFT_ROOT]: rootNode };

  const addBlockNode = (block, parentId) => {
    if (!block) return null;
    const blockId = getBlockId(block);
    const resolvedName = BLOCK_RESOLVER_NAMES[block.type] || 'ParagraphBlock';
    const isCanvas = block.type === 'row' || block.type === 'column';
    nodes[blockId] = {
      type: { resolvedName },
      isCanvas,
      props: block.props || {},
      nodes: [],
      linkedNodes: {},
      parent: parentId,
      displayName: resolvedName
    };
    const children = Array.isArray(block.children) ? block.children : [];
    if (isCanvas && children.length > 0) {
      nodes[blockId].nodes = children
        .map((child) => addBlockNode(child, blockId))
        .filter(Boolean);
    }
    return blockId;
  };

  validBlocks.forEach((block) => {
    const id = addBlockNode(block, CRAFT_ROOT);
    if (id) rootNode.nodes.push(id);
  });

  if (validBlocks.length > 0) {
    rootNode.props = { id: 'proposal-canvas', is: 'div', style: {} };
  }

  return JSON.stringify(nodes);
}

/**
 * Convert craft.js serialized JSON back to our blocks format.
 * Accepts object (from query.serialize()) or JSON string.
 */
export function craftJsonToBlocks(jsonOrString) {
  if (jsonOrString == null) return [];
  try {
    const data = typeof jsonOrString === 'string' ? JSON.parse(jsonOrString) : jsonOrString;
    if (!data || typeof data !== 'object') return [];
    const root = data[CRAFT_ROOT];
    if (!root || !root.nodes || !Array.isArray(root.nodes)) return [];

    const resolverToType = Object.fromEntries(
      Object.entries(BLOCK_RESOLVER_NAMES).map(([k, v]) => [v, k])
    );

    const buildBlock = (id) => {
      const node = data[id];
      if (!node) return null;
      const resolvedName = node.type?.resolvedName;
      const type =
        resolverToType[resolvedName] ||
        LEGACY_RESOLVER_TO_TYPE[resolvedName] ||
        'paragraph';
      const children = Array.isArray(node.nodes)
        ? node.nodes.map((childId) => buildBlock(childId)).filter(Boolean)
        : [];
      return {
        id,
        type,
        props: node.props || {},
        ...(children.length > 0 ? { children } : {})
      };
    };

    return root.nodes
      .map((id) => buildBlock(id))
      .filter(Boolean)
      .filter((b) => !AUTO_APPENDED_BLOCK_TYPES.includes(b.type));
  } catch {
    return [];
  }
}
